import { Link } from "react-router-dom";
import {
  ArrowRight,
  Network,
  Shield,
  Server,
  ClipboardCheck,
  GraduationCap,
  Wifi,
  Store,
  Rocket,
  Building2,
  ShieldCheck,
  HeartPulse,
  Landmark,
  FileCheck2,
  Briefcase,
  ChevronRight,
} from "lucide-react";
import NetworkLines from "@/components/NetworkLines";
import ExpertiseStrip from "@/components/ExpertiseStrip";
import BrandPillars from "@/components/BrandPillars";
import { SERVICES, INDUSTRIES, COMPANY } from "@/data/site";

const ICONS = {
  Network,
  Shield,
  Server,
  ClipboardCheck,
  GraduationCap,
  Wifi,
  Store,
  Rocket,
  Building2,
  ShieldCheck,
  HeartPulse,
  Landmark,
  FileCheck2,
  Briefcase,
};

const HERO_IMG =
  "https://images.unsplash.com/photo-1695668548342-c0c1ad479aee?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwyfHxkYXJrJTIwZGF0YSUyMGNlbnRlciUyMHNlcnZlciUyMHJhY2tzfGVufDB8fHx8MTc4MjQwNTg4Mnww&ixlib=rb-4.1.0&q=85";

export default function Home() {
  return (
    <div data-testid="page-home">
      {/* HERO */}
      <section data-testid="home-hero" className="relative overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_IMG})` }}
          aria-hidden="true"
        />
        <div className="absolute inset-0 bek-hero-overlay" aria-hidden="true" />
        <div className="absolute inset-0 bek-grid opacity-30" aria-hidden="true" />
        <NetworkLines className="absolute inset-0 w-full h-full opacity-50" />

        <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 pt-24 pb-32 sm:pt-32 sm:pb-40">
          <div className="max-w-3xl">
            <div className="bek-eyebrow flex items-center gap-3 mb-6">
              <span className="w-8 h-px bg-slate-300" />
              {COMPANY.tagline}
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl text-white font-bold leading-[1.05] tracking-tight">
              Network & Cybersecurity Consulting for{" "}
              <span className="bek-chrome">Secure, Reliable</span>{" "}
              Infrastructure.
            </h1>
            <p className="mt-8 text-lg text-slate-300 leading-relaxed max-w-2xl">
              We help organizations design, secure, and operate the networks their business depends on —
              from firewalls and segmentation to Linux, VMware, and audit-ready documentation.
            </p>
            <div className="mt-10 flex flex-wrap gap-4 items-center">
              <Link
                to="/contact"
                data-testid="hero-request-consultation-btn"
                className="bek-cta"
              >
                Request Consultation <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/services"
                data-testid="hero-explore-services-btn"
                className="bek-cta-secondary"
              >
                Explore Services
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* BRAND PILLARS */}
      <BrandPillars />

      {/* PRACTICE AREAS */}
      <ExpertiseStrip />

      {/* SERVICES OVERVIEW */}
      <section data-testid="home-services" className="py-24 sm:py-32 bek-radial-glow">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-16">
            <div className="max-w-2xl">
              <div className="bek-eyebrow mb-4">What We Do</div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl text-white font-semibold tracking-tight">
                {SERVICES.length} focused services.{" "}
                <span className="bek-chrome">One integrated practice.</span>
              </h2>
              <p className="mt-5 text-slate-400 leading-relaxed">
                Our consulting work is built around how modern infrastructure actually fails — and how
                to make it stop failing. Every engagement is grounded in hands-on engineering, not slideware.
              </p>
            </div>
            <Link
              to="/services"
              data-testid="home-services-cta"
              className="text-slate-300 hover:text-slate-200 text-sm font-medium tracking-wide inline-flex items-center gap-2"
            >
              View all services <ChevronRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERVICES.map((s, i) => {
              const Icon = ICONS[s.icon];
              return (
                <Link
                  key={s.id}
                  to="/services"
                  data-testid={`home-service-card-${s.id}`}
                  className="group bg-[#0B1220] border border-slate-800 p-8 bek-card-hover relative overflow-hidden"
                >
                  <div className="absolute top-0 right-0 font-mono-tech text-xs text-slate-700 p-4">
                    0{i + 1}
                  </div>
                  <div className="w-12 h-12 grid place-items-center border border-slate-300/30 mb-6">
                    <Icon className="w-6 h-6 text-slate-300" />
                  </div>
                  <h3 className="text-xl text-white font-semibold mb-3 group-hover:text-slate-300 transition-colors">
                    {s.title}
                  </h3>
                  <p className="text-sm text-slate-400 leading-relaxed">{s.short}</p>
                  <div className="mt-6 flex items-center gap-2 text-xs text-slate-300 font-mono-tech tracking-widest uppercase opacity-0 group-hover:opacity-100 transition-opacity">
                    Learn more <ArrowRight className="w-3 h-3" />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* INDUSTRIES */}
      <section data-testid="home-industries" className="py-24 sm:py-32 bg-[#060B15] border-y border-slate-800/80">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="max-w-2xl mb-16">
            <div className="bek-eyebrow mb-4">Industries Served</div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl text-white font-semibold tracking-tight">
              From startups to regulated <span className="bek-chrome">enterprise environments.</span>
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {INDUSTRIES.map((ind) => {
              const Icon = ICONS[ind.icon];
              return (
                <div
                  key={ind.name}
                  data-testid={`industry-tile-${ind.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className="border border-slate-800 bg-[#080F1C] p-6 bek-card-hover"
                >
                  <Icon className="w-5 h-5 text-slate-300 mb-4" />
                  <div className="text-white font-medium text-base">{ind.name}</div>
                  <div className="text-xs text-slate-500 mt-2 leading-relaxed">{ind.desc}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section data-testid="home-cta" className="py-24 sm:py-32 relative overflow-hidden">
        <div className="absolute inset-0 bek-grid opacity-30" aria-hidden="true" />
        <div className="absolute inset-0 bek-radial-glow" aria-hidden="true" />
        <div className="relative max-w-5xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
          <div className="bek-eyebrow mb-5 flex items-center justify-center gap-3">
            <span className="w-8 h-px bg-slate-300" /> Ready When You Are
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl text-white font-semibold tracking-tight max-w-3xl mx-auto">
            Let's talk about the network you actually need to run.
          </h2>
          <p className="mt-6 text-slate-400 max-w-2xl mx-auto leading-relaxed">
            Whether you're planning a firewall migration, hardening segmentation, or preparing for audit —
            we'll bring the engineering depth.
          </p>
          <div className="mt-10 flex flex-wrap gap-4 justify-center">
            <Link to="/contact" data-testid="home-cta-request" className="bek-cta">
              Request Consultation <ArrowRight className="w-4 h-4" />
            </Link>
            <a href={COMPANY.phoneHref} data-testid="home-cta-call" className="bek-cta-secondary">
              Call {COMPANY.phone}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
